import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { BuildLine, BuildReveal, FirstFrame, frameBeat } from '../build';
import type { BuildLineVM, CanvasVM, DisplayAction } from '../build';
import { createDomAdapter, InspectLayer, type NativeInspectMap } from '../inspect';
import { useFrameInspectAdapter } from '../inspect/frame-inspect';
import type { FrameManifest } from '../inspect/adapters/frame';
import type { InspectAdapter, InspectSession } from '../inspect';
import { CheckedCard } from './checked-card.tsx';
import type { ArtifactKind, CheckedVM, RegisteredKind } from './view-models/types.ts';
import './artifact-frame.css';

// The frame around an Artifact: the header, the build line, the canvas and the
// overlay. It only composes. The build line, first frame and reveal are DES2's, the
// inspect layer is DES2's over INS's session, the Checked card is UI1's and goes in
// the reveal's summary slot. Nothing here decides what a build or a check means.
//
// Geometry contract (DES2): the inspect layer is a sibling of the canvas scroller,
// inside a wrapper that does not scroll and is position:relative, never inside the
// scroller. The DOM adapter measures against the scroller (viewport) and looks for
// `data-inspect-node` inside the viewer's content (root).

type DomSurface = Exclude<InspectAdapter['surface'], 'frame'>;

/**
 * The inspector surface a typed kind renders on in the host page. A kind that
 * renders in a sandboxed frame (page, site) or has no typed viewer returns null: the
 * caller supplies a frame adapter for those instead.
 */
export function domSurface(kind: RegisteredKind): DomSurface | null {
  switch (kind) {
    case 'report':
    case 'dashboard':
      return 'report';
    case 'deck':
    case 'document':
    case 'spreadsheet':
    case 'table':
    case 'chart':
      return kind;
    default:
      return null;
  }
}

/**
 * The synthetic device-farm hook (T1-INS). The host controller of a synthetic build
 * supplies the case identity; the frame adds what only the real viewer knows. Never
 * passed in production, so nothing is rendered there. All values are scalars.
 */
export type DeviceObservationInput = {
  syntheticOnly: true;
  caseId: string;
  revisionId: string;
  buildId: string;
  /** Business effects the controller observed (always 0 for a viewer-only case). */
  businessEffects: number;
};

/**
 * Inspection of a Page or Site in a sandboxed frame (DES2's useFrameInspectAdapter):
 * RUN's transport from the mounted runtime, INS's domain session and manifest
 * projection, and a ref to the iframe (RuntimeFrame's `frameRef`). The hook owns the
 * bridge and adapter and disposes them; the frame only mounts the layer it returns.
 */
export type FrameInspectInput = {
  transport: Parameters<typeof useFrameInspectAdapter>[0]['transport'];
  domain: Parameters<typeof useFrameInspectAdapter>[0]['domain'];
  manifest?: FrameManifest;
  frame: RefObject<HTMLElement | null>;
};

export type ArtifactFrameProps = {
  kind: ArtifactKind | RegisteredKind;
  /** The Artifact's title and kind chip, as the caller composes them. */
  heading: ReactNode;
  /** UI2's Export / Versions / Publish / Send controls, placed as given. */
  toolbar?: ReactNode;
  build?: BuildLineVM | null;
  /** A build line action (stop, continue, fix): ART's typed action handle goes back up. */
  onBuildAction?: (action: DisplayAction) => void;
  /** The kind word for "{kind} is ready", from the kind registry. */
  kindLabel?: string | null;
  /** Building/preview VM. An already-loaded saved display needs no build claim. */
  canvas?: CanvasVM | null;
  /** The skeleton shown until the first fragment; drawn from the plan by the caller. */
  skeleton?: ReactNode;
  /** The viewer, already rendering the canvas VM's fragments. */
  children: ReactNode;
  checked?: CheckedVM | null;
  /** The key of the reveal: the saved revision. Absent means no reveal. */
  revealKey?: string | null;
  versionLabel?: string;
  resolveKey?: (key: string) => string;
  /** INS's session. Without one there is no Inspect toggle and no layer. */
  inspect?: {
    session: InspectSession;
    toggle: ReactNode;
    adapter?: InspectAdapter;
    phone?: boolean;
    /** Phone: focus the composer with the selection attached (the sheet's Mention). */
    onMention?: () => void;
    /** An unsupported direct edit: hand the selection to the chat. */
    onHandoff?: () => void;
    /**
     * INS's public native-inspection map (pidFor/canonicalFor) for the host-DOM kinds. Null or
     * absent means native nodes are unavailable by design until INS publishes it; the frame
     * never builds a map of its own.
     */
    nativeMap?: NativeInspectMap | null;
    /** INS's manifest view (keyboard list and Select parent) for the host-DOM kinds. */
    view?: Parameters<typeof createDomAdapter>[0]['view'];
  } | null;
  /** Synthetic device-farm builds only: renders [data-artifact-device-observation]. */
  deviceObservation?: DeviceObservationInput | null;
  /** Page/Site: wires the inspector to the sandboxed frame instead of the host DOM. */
  frameInspect?: FrameInspectInput | null;
  /** Test seam: the host-DOM adapter factory (defaults to DES2's createDomAdapter). */
  createAdapter?: typeof createDomAdapter;
};

export function ArtifactFrame({
  kind,
  heading,
  toolbar,
  build,
  onBuildAction,
  kindLabel,
  canvas,
  skeleton,
  children,
  checked,
  revealKey,
  versionLabel,
  resolveKey,
  inspect,
  deviceObservation,
  frameInspect,
  createAdapter,
}: ArtifactFrameProps) {
  const canvasWrapper = useRef<HTMLDivElement>(null);
  const noFrame = useRef<HTMLElement | null>(null);
  const wired = useFrameInspectAdapter({
    transport: frameInspect?.transport ?? null,
    domain: frameInspect?.domain ?? null,
    session: inspect?.session ?? { sessionId: '', revision: '' },
    frame: frameInspect?.frame ?? noFrame,
    overlay: canvasWrapper,
    manifest: frameInspect?.manifest,
  });
  const frame = useRef<HTMLElement>(null);
  const [primaryActions, setPrimaryActions] = useState(0);
  // Primary actions the person can see in this frame: the kit marks a primary button
  // data-variant="primary". Counted after layout, on every render.
  useLayoutEffect(() => {
    const count = frame.current?.querySelectorAll('[data-variant="primary"]').length ?? 0;
    setPrimaryActions((was) => (was === count ? was : count));
  });
  // The scroller and the viewer's content element are tracked as state, by callback ref:
  // the content only mounts once the first frame has content, and it is replaced when the
  // reveal wrapper takes over the same viewer, so the adapter is bound to the elements that
  // are actually mounted now (a stable `inspect` object across loading -> ready must not
  // leave no adapter, nor an adapter reading a detached root).
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);
  const surface = domSurface(kind);
  const [adapter, setAdapter] = useState<InspectAdapter | null>(null);
  const supplied = inspect?.adapter ?? null;
  const nativeMap = inspect?.nativeMap ?? null;
  const view = inspect?.view ?? null;
  const wantsDom = Boolean(inspect) && !supplied;
  const makeAdapter = createAdapter ?? createDomAdapter;
  useLayoutEffect(() => {
    if (supplied) return setAdapter(supplied);
    if (!wantsDom || !surface || !scroller || !contentEl) return setAdapter(null);
    const made = makeAdapter({
      surface,
      root: contentEl,
      viewport: scroller,
      map: nativeMap,
      view,
    });
    setAdapter(made);
    return () => {
      (made as { dispose?: () => void }).dispose?.();
    };
  }, [supplied, wantsDom, surface, scroller, contentEl, makeAdapter, nativeMap, view]);

  const content = (
    <div
      ref={setContentEl}
      className="ws-frame-content"
      data-kind={kind}
      data-testid="artifact-content"
    >
      {children}
    </div>
  );
  const viewer = canvas ? (
    <FirstFrame vm={canvas} skeleton={skeleton} content={content} />
  ) : (
    content
  );
  const summary = checked ? (
    <div data-testid="artifact-checked">
      <CheckedCard vm={checked} versionLabel={versionLabel} resolveKey={resolveKey} />
    </div>
  ) : null;
  return (
    <section className="ws-frame" data-kind={kind} data-testid="artifact-frame" ref={frame}>
      {/* An ordinary header stays; one with no heading, toolbar or toggle has nothing to show. */}
      {(heading || toolbar || inspect?.toggle) && (
        <header className="ws-frame-head" data-testid="artifact-frame-head">
          <div className="ws-frame-heading">{heading}</div>
          <div className="ws-frame-toolbar" data-testid="artifact-frame-toolbar">
            {inspect?.toggle}
            {toolbar}
          </div>
        </header>
      )}
      {build && (
        <BuildLine
          vm={build}
          variant="bar"
          phone={inspect?.phone ?? false}
          kindLabel={kindLabel}
          labelFor={resolveKey}
          onAction={onBuildAction ?? (() => {})}
        />
      )}
      <div className="ws-frame-canvas" data-testid="artifact-canvas" ref={canvasWrapper}>
        <div className="ws-frame-scroll" ref={setScroller} data-testid="artifact-scroll">
          {revealKey ? (
            <BuildReveal revealKey={revealKey} summary={summary}>
              {viewer}
            </BuildReveal>
          ) : (
            viewer
          )}
        </div>
        {inspect && !frameInspect && adapter && (
          <InspectLayer
            adapter={adapter}
            session={inspect.session}
            phone={inspect.phone}
            onMention={inspect.onMention}
            onHandoff={inspect.onHandoff}
          />
        )}
        {inspect && frameInspect && wired && (
          <InspectLayer
            adapter={wired.adapter}
            clock={wired.clock}
            session={inspect.session}
            phone={inspect.phone}
            onMention={inspect.onMention}
            onHandoff={inspect.onHandoff}
          />
        )}
      </div>
      {deviceObservation && (
        <div
          hidden
          data-artifact-device-observation={JSON.stringify({
            syntheticOnly: true,
            source: 'host-controller',
            caseId: deviceObservation.caseId,
            revisionId: deviceObservation.revisionId,
            buildId: deviceObservation.buildId,
            kind,
            realViewer: true,
            state: revealKey ? 'revealed' : canvas ? frameBeat(canvas) : 'content',
            primaryActionCount: primaryActions,
            businessEffects: deviceObservation.businessEffects,
          })}
        />
      )}
    </section>
  );
}
